import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowRight, Building2, Package, Store, Users, Megaphone, Briefcase, Truck } from "lucide-react";

const BUSINESS_TYPES = [
  { id: "dropshipping", label: "Dropshipping", icon: Package, roles: ["merchant"], description: "Sell curated products from Vendora's supplier network." },
  { id: "wholesale", label: "Wholesale / Supplier", icon: Building2, roles: ["supplier"], description: "List products other merchants can resell." },
  { id: "affiliate", label: "Affiliate", icon: Megaphone, roles: ["affiliate"], description: "Promote products and earn commissions." },
  { id: "creator", label: "Creator Store", icon: Store, roles: ["creator", "affiliate"], description: "Curate a personal storefront and earn dual revenue." },
  { id: "private_label", label: "Private Label / Merchant", icon: Store, roles: ["merchant"], description: "Sell your own products with a branded storefront." },
  { id: "service_provider", label: "Business Service", icon: Briefcase, roles: ["service_provider"], description: "Offer accounting, marketing, legal, IT services." },
  { id: "fulfillment", label: "Fulfillment Partner", icon: Truck, roles: ["fulfillment_partner"], description: "Receive and ship orders for merchants." },
  { id: "customer", label: "I'm just shopping", icon: Users, roles: ["customer"], description: "Buy products from Vendora storefronts." },
];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [type, setType] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", full_name: "", business_name: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!type) { toast.error("Choose a business type"); setStep(1); return; }
    setSubmitting(true);
    try {
      await register({ ...form, roles: type.roles });
      toast.success("Welcome to Vendora");
      nav("/app");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b border-border h-16 flex items-center px-6">
        <Link to="/"><Logo size={32} /></Link>
        <div className="ml-auto text-sm text-muted-foreground">
          Already have an account? <Link to="/login" className="text-primary font-medium" data-testid="register-login-link">Sign in</Link>
        </div>
      </header>

      <div className="flex-1 grid lg:grid-cols-12 max-w-7xl mx-auto px-6 py-12 gap-12 w-full">
        <div className="lg:col-span-4">
          <div className="label-eyebrow text-secondary mb-2">Business Setup Wizard</div>
          <h1 className="font-display text-4xl font-black tracking-tighter text-primary leading-none">Get set up in 60 seconds.</h1>
          <p className="mt-4 text-muted-foreground">Pick what you're building. We'll configure your portal, permissions, and dashboards for you.</p>
          <ol className="mt-10 space-y-4 text-sm">
            <Step n={1} active={step === 1} done={step > 1} label="Business type" />
            <Step n={2} active={step === 2} done={step > 2} label="Account details" />
            <Step n={3} active={step === 3} done={false} label="Launch" />
          </ol>
        </div>

        <div className="lg:col-span-8">
          {step === 1 && (
            <div>
              <h2 className="font-display text-2xl font-bold text-primary">What are you building on Vendora?</h2>
              <div className="mt-6 grid sm:grid-cols-2 gap-3">
                {BUSINESS_TYPES.map((b) => (
                  <button
                    key={b.id}
                    onClick={() => setType(b)}
                    data-testid={`bizType-${b.id}`}
                    className={`text-left v-card flex flex-col gap-2 transition-all ${type?.id === b.id ? "border-secondary border-2 shadow-md" : ""}`}
                  >
                    <b.icon size={24} className={type?.id === b.id ? "text-secondary" : "text-primary"} strokeWidth={1.75} />
                    <div className="font-display font-semibold text-primary mt-2">{b.label}</div>
                    <div className="text-xs text-muted-foreground">{b.description}</div>
                  </button>
                ))}
              </div>
              <div className="mt-8 flex justify-end">
                <button disabled={!type} onClick={() => setStep(2)} className="v-btn-primary" data-testid="register-step1-next">
                  Continue <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <form onSubmit={onSubmit} className="max-w-xl">
              <h2 className="font-display text-2xl font-bold text-primary">Tell us about you.</h2>
              <p className="text-sm text-muted-foreground mt-1">Building as <span className="font-semibold text-primary">{type?.label}</span>.</p>
              <div className="mt-8 grid gap-4">
                <Field label="Full name" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} testid="register-fullname" required />
                <Field label="Business name (optional)" value={form.business_name} onChange={(v) => setForm({ ...form, business_name: v })} testid="register-business" />
                <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="register-email" required />
                <Field label="Phone (optional)" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="register-phone" />
                <Field label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="register-password" required />
              </div>
              <div className="mt-8 flex justify-between">
                <button type="button" onClick={() => setStep(1)} className="v-btn-outline" data-testid="register-back">Back</button>
                <button type="submit" disabled={submitting} className="v-btn-primary" data-testid="register-submit">
                  {submitting ? "Creating…" : "Create my account"} <ArrowRight size={16} />
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Step({ n, active, done, label }) {
  return (
    <li className={`flex items-center gap-3 ${active ? "text-primary font-semibold" : done ? "text-accent-green" : "text-muted-foreground"}`}>
      <span className={`w-7 h-7 inline-flex items-center justify-center rounded-sm border ${active ? "border-secondary bg-secondary text-white" : done ? "border-accent-green bg-accent-green text-white" : "border-border"}`}>{n}</span>
      {label}
    </li>
  );
}

function Field({ label, type = "text", value, onChange, testid, required }) {
  return (
    <div>
      <label className="label-eyebrow text-foreground mb-2 block">{label}</label>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="v-input" data-testid={testid} />
    </div>
  );
}
